// The sidebar's "Not a competitor" toggle (GRO-425, D6): lifts the competitor flag, the one hard
// override in scoring, on the next score. Shown under Scores when the account is flagged or already
// overridden. Vikram, leadership and strategists may change it (the server says so in `can_edit`);
// everyone else sees the state read-only. Each change is stored with who, when and why.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCompetitorOverride, panelMessage, postCompetitorOverride } from '../api/client';
import type { CompetitorOverridePayload, CompetitorOverrideState } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';

function changedLine(state: CompetitorOverrideState): string | null {
  if (!state.changed_at) return null;
  const answer = state.not_competitor ? 'Marked not a competitor' : 'Marked a competitor again';
  const because = state.reason ? ` (“${state.reason}”)` : '';
  return `${answer} by ${state.changed_by_email ?? 'someone'} on ${state.changed_at.slice(0, 10)}${because}.`;
}

// The score shown is from the last run; the toggle's answer applies from the next one.
function statusLine(flagged: boolean, notCompetitor: boolean): string {
  if (flagged && !notCompetitor) return 'Flagged as a competitor: Fit is held to low. Tick to lift the flag from the next score.';
  if (flagged) return 'Marked not a competitor: the flag lifts on the next score.';
  if (notCompetitor) return 'The flag is lifted: Fit is scored normally.';
  return 'Marked a competitor again: the flag returns on the next score.';
}

export function CompetitorToggle({ accountId, flagged }: { accountId: string; flagged: boolean }) {
  const { getToken } = useAuthContext();
  const queryClient = useQueryClient();
  const queryKey = ['competitor-override', accountId];
  const [reason, setReason] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  const overrideQuery = useQuery({
    queryKey,
    queryFn: () => fetchCompetitorOverride(getToken, accountId),
    retry: false,
  });
  const save = useMutation({
    mutationFn: (notCompetitor: boolean) =>
      postCompetitorOverride(getToken, accountId, { not_competitor: notCompetitor, reason: reason.trim() || undefined }),
    onSuccess: (payload: CompetitorOverridePayload) => {
      queryClient.setQueryData(queryKey, payload);
      setReason('');
      setSaveError(null);
    },
    onError: (error: unknown) => setSaveError(panelMessage(error, 'Not saved:')),
  });

  if (overrideQuery.isPending) return <div className="competitortoggle"><span className="ctnote">Loading the competitor flag…</span></div>;
  if (overrideQuery.isError) {
    return <div className="competitortoggle"><span className="ctnote err">The competitor flag could not be loaded.</span></div>;
  }

  const { override, can_edit: canEdit } = overrideQuery.data;
  const checked = override.not_competitor;
  const changed = changedLine(override);
  const status = statusLine(flagged, checked);
  return (
    <div className="competitortoggle" data-testid="competitor-toggle">
      <div className="ctrow">
        <label className={canEdit ? '' : 'readonly'}>
          <input
            type="checkbox"
            checked={checked}
            disabled={!canEdit || save.isPending}
            onChange={event => save.mutate(event.target.checked)}
          />
          Not a competitor
        </label>
        {canEdit ? (
          <input
            type="text"
            value={reason}
            maxLength={500}
            placeholder="Reason (optional)"
            aria-label="Reason for the change (optional)"
            disabled={save.isPending}
            onChange={event => setReason(event.target.value)}
          />
        ) : null}
      </div>
      {status ? <span className="ctnote">{status}</span> : null}
      {changed ? <span className="ctnote">{changed}</span> : null}
      {canEdit ? null : <span className="ctnote">Only Vikram, leadership and strategists can change this.</span>}
      {saveError ? <span className="ctnote err">{saveError}</span> : null}
    </div>
  );
}
